import { useState, useEffect } from "react";
import { LayoutDashboard, BarChart3, Download, Users, Settings, LogOut } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import ConfirmDialog from "./ConfirmDialog";
import api from "../services/api";

export default function ViewerSidebar() {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  useEffect(() => {
    const hydrateUser = async () => {
      const storedUser = JSON.parse(localStorage.getItem("user"));
      if (storedUser) {
        setUser(storedUser);
      }

      try {
        const res = await api.get("/auth/me");
        const currentUser = res.data?.user;
        if (currentUser) {
          setUser(currentUser);
          localStorage.setItem("user", JSON.stringify(currentUser));
        }
      } catch (error) {
        console.error("Failed to load current user profile:", error);
      }
    };

    hydrateUser();
  }, []);

  const menu = [
    { label: "Document Tracker", icon: <LayoutDashboard size={18} />, path: "/viewer-dashboard" },
    { label: "Download All ESMPs", icon: <Download size={18} />, path: "/viewer/download-all" },
    { label: "Statistics & Reports", icon: <BarChart3 size={18} />, path: "/viewer/statistics" },
    { label: "Sector Statistics", icon: <BarChart3 size={18} />, path: "/viewer/statistics/sectors" },
    { label: "Reviewer Workload", icon: <Users size={18} />, path: "/viewer/reviewer-workload" },
    { label: "Settings", icon: <Settings size={18} />, path: "/viewer/settings" },
  ];

  const confirmLogout = () => {
    setShowLogoutDialog(false);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    navigate("/");
  };

  return (
    <aside className="w-72 bg-white min-h-screen shadow-lg flex flex-col">
      <div className="px-5 py-6 bg-gradient-to-r from-green-800 to-green-600 text-white">
        <div className="flex items-center gap-3">
          <div className="bg-white text-green-700 rounded-full w-10 h-10 flex items-center justify-center font-bold">
            {user?.name ? user.name[0].toUpperCase() : "V"}
          </div>
          <div>
            <h3 className="text-sm font-semibold">{user?.name || "Viewer"}</h3>
            <p className="text-xs text-green-200">Read only access</p>
          </div>
        </div>
      </div>

      <nav className="px-4 py-4 flex-1">
        <ul className="space-y-2 text-sm">
          {menu.map((item, i) => {
            const isActive = location.pathname === item.path;
            return (
              <li
                key={i}
                onClick={() => navigate(item.path)}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg cursor-pointer transition-all ${
                  isActive
                    ? "bg-green-100 text-green-700 font-semibold border-l-4 border-green-600"
                    : "text-gray-700 hover:bg-green-50 hover:text-green-700"
                }`}
              >
                <span className="flex items-center">{item.icon}</span>
                <span>{item.label}</span>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="border-t px-5 py-4 space-y-3">
        <button
          className="text-sm text-gray-600 hover:text-green-700 transition"
          onClick={() => alert("Support is available through your administrator")}
        >
          Help
        </button>

        <button
          className="flex items-center gap-2 text-sm text-red-500 hover:text-red-600 transition"
          onClick={() => setShowLogoutDialog(true)}
        >
          <LogOut size={18} />
          Log out
        </button>
      </div>

      <ConfirmDialog
        open={showLogoutDialog}
        title="Confirm Logout"
        message="Are you sure you want to logout?"
        confirmText="Logout"
        cancelText="Cancel"
        onConfirm={confirmLogout}
        onCancel={() => setShowLogoutDialog(false)}
      />
    </aside>
  );
}
